import api from './api.service';
import type { RejectionPayload } from './rejection.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface ReturnableSourceLine {
  grn_id: string;
  grn_number: string;
  grn_item_id: string;
  po_item_id: string;
  item_code: string | null;
  item_description: string | null;
  supplier_description?: string | null;
  specifications?: string | null;
  accepted_quantity: number;
  billed_quantity: number;
  returned_quantity: number;
  returnable_quantity: number;
  uom: string | null;
  unit_rate: number;
  project_id: string | null;
  warehouse_id: string | null;
  line_number: number;
}

export interface PurchaseReturnLineDTO {
  id: string;
  purchase_return_id: string;
  grn_item_id?: string;
  line_number: number;
  item_code?: string;
  item_description?: string;
  supplier_description?: string | null;
  specifications?: string | null;
  return_quantity: number;
  unit_rate: number;
  amount: number;
  /** Base receipt value this line reversed when posted (from its journal lines); null before posting. */
  reversed_value_base?: number | null;
  return_reason?: string;
  uom?: string;
}

export interface PurchaseReturnDTO {
  id: string;
  return_number: string;
  return_date: string;
  supplier_id: string;
  grn_id: string;
  status: string;
  approval_instance_id?: string | null;
  return_type?: string;
  warehouse_id?: string;
  project_id?: string;
  department_id?: string | null;
  return_reason?: string;
  reference?: string;
  remarks?: string;
  is_posted?: boolean;
  posted_date?: string;
  journal_voucher_id?: string;
  supplier?: { id: string; supplier_name?: string };
  goodsReceiptNote?: { id: string; grn_number?: string };
  lines?: PurchaseReturnLineDTO[];
}

export interface PurchaseReturnFilters {
  supplier_id?: string;
  grn_id?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
}

export const purchaseReturnService = {
  getReturnableSources: (params?: { supplier_id?: string; grn_id?: string }) =>
    api.get('/procurement/purchase-returns/returnable-sources', { params }).then(unwrap<ReturnableSourceLine[]>),

  list: (filters?: PurchaseReturnFilters) =>
    api.get('/procurement/purchase-returns', { params: filters }).then(unwrap<PurchaseReturnDTO[]>),

  findById: (id: string) =>
    api.get(`/procurement/purchase-returns/${id}`).then(unwrap<PurchaseReturnDTO>),

  create: (body: {
    return_date: string;
    supplier_id: string;
    grn_id: string;
    return_type?: string;
    warehouse_id?: string;
    project_id?: string;
    department_id?: string;
    return_reason?: string;
    reference?: string;
    remarks?: string;
    lines: Array<{ grn_item_id: string; return_quantity: number; return_reason?: string }>;
  }) =>
    api.post('/procurement/purchase-returns', body).then(unwrap<PurchaseReturnDTO>),

  update: (id: string, body: Partial<{ return_date: string; department_id?: string; return_reason?: string; reference?: string; remarks?: string; lines: Array<{ grn_item_id: string; return_quantity: number; return_reason?: string }> }>) =>
    api.put(`/procurement/purchase-returns/${id}`, body).then(unwrap<PurchaseReturnDTO>),

  submit: (id: string) =>
    api.post(`/procurement/purchase-returns/${id}/submit`).then(unwrap<PurchaseReturnDTO>),

  approve: (id: string, remarks?: string) =>
    api
      .post(`/procurement/purchase-returns/${id}/approve`, { remarks: remarks ?? undefined })
      .then(unwrap<PurchaseReturnDTO>),

  rejectApproval: (id: string, payload: RejectionPayload) =>
    api.post(`/procurement/purchase-returns/${id}/reject-approval`, payload).then(unwrap<PurchaseReturnDTO>),

  post: (id: string) =>
    api.post(`/procurement/purchase-returns/${id}/post`).then(unwrap<PurchaseReturnDTO>),

  cancel: (id: string) =>
    api.post(`/procurement/purchase-returns/${id}/cancel`).then(unwrap<PurchaseReturnDTO>),
};
